import { ReactNode } from "react";
import { useTranslations } from "use-intl";
import {
  IncidentListQueryVariables,
  QueryIncidentsOrderByColumn,
  QueryIncidentsOrderByOrderByClause,
  SortOrder,
} from "@/gql/graphql";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
  GetQueryVariablesFn,
} from "@/lib/Components/DataTable/DataTable";
import { CellContext } from "@/lib/Components/DataTable/types";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { Link } from "@/components/catalyst/link";
import { Highlighter } from "@/lib/Components/Highlighter";
import { GenericErrorBoundary } from "@/lib/Components/Layout/ErrorBoundary/ErrorBoundary";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatLocalDate } from "@/lib/Formatters/formatDate";
import type { TableId } from "@/app/Common/Utils/table-id";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import {
  IncidentListItem,
  incidentListQuery,
} from "@/app/Incidents/GraphQL/incidentListQuery";
import { IncidentStatusBadge } from "@/app/Incidents/Components/IncidentStatusBadge";

type IncidentTableColumn =
  | "reference"
  | "found_at"
  | "supplier"
  | "driver"
  | "work_order"
  | "type"
  | "status"
  | "claim"
  | "excess";

type IncidentTableFilterVariables = Omit<
  IncidentListQueryVariables,
  "page" | "first" | "orderBy" | "search"
>;

const sortableColumns: Partial<
  Record<IncidentTableColumn, QueryIncidentsOrderByColumn>
> = {
  reference: QueryIncidentsOrderByColumn.Reference,
  found_at: QueryIncidentsOrderByColumn.FoundAt,
  status: QueryIncidentsOrderByColumn.Status,
};

export function IncidentsTable({
  id,
  filterVariables,
  removedColumns = [],
  search,
  onSearchChange,
  page,
  onPageChange,
  filters,
  rightButtons,
  emptyState,
  searchable = true,
}: {
  id: TableId;
  filterVariables: IncidentTableFilterVariables;
  removedColumns?: IncidentTableColumn[];
  search?: string;
  onSearchChange?: (search: string) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  filters?: ReactNode;
  rightButtons?: ReactNode;
  emptyState?: ReactNode;
  searchable?: boolean;
}) {
  "use no memo";
  const t = useTranslations("incident");
  const { isAdmin } = useTenant();

  function isRemoved(column: IncidentTableColumn) {
    return removedColumns.includes(column);
  }

  const columns: DataTableColDef<IncidentListItem, any, IncidentTableColumn>[] =
    [
      {
        id: "reference",
        accessorFn: (incident) => incident.reference,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.reference")} />
        ),
        cell: ReferenceCell,
      },
      {
        id: "found_at",
        accessorFn: (incident) => formatLocalDate(incident.found_at),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.found")} />
        ),
      },
      {
        id: "supplier",
        accessorFn: (incident) => incident.supplier.name,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.supplier")} />
        ),
        enableSorting: false,
        isHidden: !isAdmin || isRemoved("supplier"),
      },
      {
        id: "driver",
        accessorFn: (incident) => incident.proDriver.name ?? "--",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.driver")} />
        ),
        enableSorting: false,
        isHidden: isRemoved("driver"),
      },
      {
        id: "work_order",
        accessorFn: (incident) => incident.workOrder.reference,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.workOrder")} />
        ),
        cell: WorkOrderCell,
        enableSorting: false,
        isHidden: isRemoved("work_order"),
      },
      {
        id: "type",
        accessorFn: (incident) => t(`type.${incident.type}`),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.type")} />
        ),
        enableSorting: false,
      },
      {
        id: "status",
        accessorFn: (incident) => incident.status,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.status")} />
        ),
        cell: ({ row }) => (
          <IncidentStatusBadge
            status={row.original.status}
            outcome={row.original.closed_outcome}
          />
        ),
      },
      {
        id: "claim",
        accessorFn: (incident) =>
          incident.claim_opened_at ? t("table.yes") : t("table.no"),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.claim")} />
        ),
        enableSorting: false,
        isHidden: isRemoved("claim"),
      },
      {
        id: "excess",
        accessorFn: (incident) =>
          formatCurrency(incident.excess_amount, incident.currency),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("table.excess")} />
        ),
        enableSorting: false,
        isHidden: isRemoved("excess"),
      },
    ];

  return (
    <GenericErrorBoundary>
      <DataTable
        id={id}
        searchable={searchable}
        searchPlaceholder={t("table.searchPlaceholder")}
        search={search}
        onSearchChange={onSearchChange}
        page={page}
        onPageChange={onPageChange}
        accessor={(data) => data.incidents}
        columns={columns}
        document={incidentListQuery}
        initialSorting={[{ id: "found_at", desc: true }]}
        filters={filters}
        rightButtons={rightButtons}
        emptyState={emptyState}
        getQueryVariables={getIncidentListQueryVariables(filterVariables)}
      />
    </GenericErrorBoundary>
  );
}

function getIncidentListQueryVariables(
  filterVariables: IncidentTableFilterVariables,
): GetQueryVariablesFn<IncidentListQueryVariables, IncidentTableColumn> {
  return ({ pagination, sorting, search }) => {
    const orderBy: QueryIncidentsOrderByOrderByClause[] = sorting.flatMap(
      (sort) => {
        const column = sortableColumns[sort.id];

        return column
          ? [{ column, order: sort.desc ? SortOrder.Desc : SortOrder.Asc }]
          : [];
      },
    );

    return {
      ...filterVariables,
      page: pagination.pageIndex,
      first: pagination.pageSize,
      search: search || undefined,
      orderBy: orderBy.length > 0 ? orderBy : undefined,
    };
  };
}

function ReferenceCell({ row, table }: CellContext<IncidentListItem, any>) {
  "use no memo";
  const search = table.options.meta?.search ?? "";

  return (
    <ModelLinkCell
      from="/$tenant/$orgId/supplier/{-$supplierId}"
      to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents/$incidentId"
      params={(prev) => ({
        ...prev,
        incidentId: row.original.id,
      })}
    >
      <Highlighter searchString={search}>{row.original.reference}</Highlighter>
    </ModelLinkCell>
  );
}

function WorkOrderCell({ row }: CellContext<IncidentListItem, any>) {
  "use no memo";
  const workOrder = row.original.workOrder;

  return (
    <Link
      from="/$tenant/$orgId/supplier/{-$supplierId}"
      to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
      params={(prev) => ({ ...prev, id: workOrder.id })}
      className="text-blue-600 hover:text-blue-800"
    >
      {workOrder.reference}
    </Link>
  );
}
